import {
    Spotlight,
    SpotlightActionData,
    SpotlightActionGroupData,
} from "@mantine/spotlight";
import * as Sentry from "@sentry/react";
import {chatApi} from "./chatApi";
import {useTranslation} from "react-i18next";
import IconEdit from "../../assets/edit.svg?react";
import {useGoHome} from "./hooks";
import IconMessageCircle from "../../assets/message-circle.svg?react";
import IconSearch from "../../assets/search.svg?react";
import IconHistory from "../../assets/history.svg?react";
import {
    ActionIcon,
    Box,
    Center,
    Group,
    Loader,
    Skeleton,
    Stack,
    Text,
    ThemeIcon,
} from "@mantine/core";
import {useNavigate} from "react-router-dom";
import {spotlight} from "@mantine/spotlight";
import IconX from "../../assets/x.svg?react";
import {useCallback, useMemo, useState, useEffect, useRef} from "react";
import classes from "./AppSpotlight.module.css";
import {formatTimeAgo} from "../../utils/dateUtils";
import {useCSSHighlights} from "../../useCssHighlights";
import {useDebouncedValue} from "@mantine/hooks";
import scrollClasses from "./components/scrollContainer.module.css";
import {USER_THREADS_PAGE_SIZE} from "../../types";
import {ParagraphWithReferences} from "./components/stream-parser/inline/ParagraphWithReferences";

export function AppSpotlight() {
    const [startNewChat] = useGoHome();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [debouncedQuery] = useDebouncedValue(query, 500);
    const {data, isFetching} = chatApi.endpoints.getUserThreads.useQuery(
        {
            page: 0,
            pageSize: USER_THREADS_PAGE_SIZE,
        },
        {
            skip: !isOpen,
        }
    );
    const [page, setPage] = useState(0);
    const {
        data: searchResults,
        isLoading: isSearchLoading,
        isFetching: isSearchFetching,
        fulfilledTimeStamp,
        originalArgs: searchOriginalArgs,
    } = chatApi.endpoints.searchUserThreads.useQuery(
        {
            query: debouncedQuery,
            page,
            pageSize: 6,
        },
        {
            skip: debouncedQuery.length === 0,
        }
    );

    // Reset page when search query changes
    const prevDebouncedQuery = useRef(debouncedQuery);
    useEffect(() => {
        if (prevDebouncedQuery.current !== debouncedQuery) {
            if (page > 0) {
                setPage(0);
            }
            if (debouncedQuery.length > 0) {
                Sentry.metrics.count("chat.thread_search_performed");
            }
        }
        prevDebouncedQuery.current = debouncedQuery;
    }, [debouncedQuery, page]);

    const handleScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            if (isSearchFetching) return;
            const {scrollHeight, scrollTop, clientHeight} = event.currentTarget;
            if (scrollHeight - scrollTop - clientHeight <= 1) {
                if (
                    searchResults?.totalPages &&
                    page < searchResults.totalPages - 1
                ) {
                    setPage(page + 1);
                }
            }
        },
        [searchResults?.totalPages, page, isSearchFetching]
    );

    const skeletons = Array.from({length: 6}, (_, i) => (
        <Group
            key={`skeleton-${i}`}
            wrap="nowrap"
            w="100%"
            gap="xs"
            className={classes.skeletonGroup}
        >
            <Skeleton height={24} width={24} circle />
            <Stack gap={0} my="xs" style={{flex: 1}}>
                <Skeleton height={10} width={80} mb={"xs"} />
                <Skeleton height={10} width={250} />
            </Stack>
        </Group>
    ));

    const defaultListSkeletons = Array.from({length: 7}, (_, i) => (
        <Group
            key={`default-skeleton-${i}`}
            wrap="nowrap"
            w="100%"
            gap="xs"
            className={classes.skeletonGroup}
        >
            <Skeleton height={24} width={24} circle />
            <Group justify="space-between" style={{flex: 1}} wrap="nowrap">
                <Skeleton height={12} w="50%" />
                <Skeleton height={10} w="10%" />
            </Group>
        </Group>
    ));
    const renderedSkeletons = useMemo(() => {
        if (
            (query.length > 0 && isSearchFetching) ||
            (isSearchLoading && searchOriginalArgs?.query === query)
        ) {
            return skeletons;
        }
        return null;
    }, [isSearchFetching, query, searchOriginalArgs]);

    const renderedPagingLoading = useMemo(() => {
        if (
            renderedSkeletons === null &&
            query.length > 0 &&
            searchResults?.items.length &&
            searchResults.items.length > 0 &&
            isSearchFetching &&
            searchOriginalArgs?.query === query
        ) {
            return (
                <Center>
                    <Loader type="dots" color="gneis.6" size="sm" />
                </Center>
            );
        }
        return [];
    }, [isSearchLoading, query, searchOriginalArgs, renderedSkeletons]);

    const defaultList = [
        <Spotlight.Action
            key="newChat"
            onClick={() => startNewChat()}
            className={classes.spotlightAction}
        >
            <Group wrap="nowrap" w="100%" gap="xs">
                <ThemeIcon
                    color={"var(--dib-brand-base)"}
                    variant="subtle"
                    fw={400}
                    title={t("common.newChat")}
                >
                    <IconEdit width={18} />
                </ThemeIcon>
                <div style={{flex: 1}}>
                    <Text className={classes.titleText}>
                        {t("common.newChat")}
                    </Text>
                </div>
            </Group>
        </Spotlight.Action>,
        <Spotlight.ActionsGroup
            key="recentChats"
            label={t("common.recentChats")}
        >
            {isFetching
                ? defaultListSkeletons
                : data?.items.slice(0, 7).map((thread) => (
                      <Spotlight.Action
                          key={thread.threadId}
                          onClick={() => navigate(`/chat/${thread.threadId}`)}
                          className={classes.spotlightAction}
                      >
                          <Group wrap="nowrap" w="100%" gap="xs">
                              <ThemeIcon
                                  color={"var(--dib-brand-base)"}
                                  variant="subtle"
                                  fw={400}
                                  title={thread.name}
                              >
                                  <IconMessageCircle width={18} />
                              </ThemeIcon>
                              <Group
                                  justify="space-between"
                                  style={{flex: 1}}
                                  wrap="nowrap"
                              >
                                  <Text className={classes.titleText}>
                                      {thread.name}
                                  </Text>
                                  <Text
                                      opacity={0.5}
                                      size="xs"
                                      className={classes.asideText}
                                  >
                                      {formatTimeAgo(thread.lastUpdated)}
                                  </Text>
                              </Group>
                          </Group>
                      </Spotlight.Action>
                  ))}
        </Spotlight.ActionsGroup>,
    ];

    const searchResultList = searchResults?.items.map((result) => (
        <Spotlight.Action
            key={result.threadId}
            onClick={() => {
                Sentry.metrics.count("chat.thread_search_result_clicked");
                navigate(`/chat/${result.threadId}`);
            }}
            className={classes.spotlightAction}
        >
            <Group wrap="nowrap" w="100%" gap="xs">
                <ThemeIcon
                    color={"var(--dib-brand-base)"}
                    variant="subtle"
                    fw={400}
                    title={result.threadName}
                >
                    <IconMessageCircle width={18} />
                </ThemeIcon>
                <Stack
                    justify="space-between"
                    style={{flex: 1, overflow: "hidden"}}
                    gap={0}
                >
                    <Group
                        justify="space-between"
                        style={{flex: 1}}
                        wrap="nowrap"
                    >
                        <Text
                            data-search-results
                            c={"gneis.9"}
                            className={classes.titleText}
                        >
                            {result.threadName}
                        </Text>
                        <Text
                            opacity={0.5}
                            size="xs"
                            className={classes.asideText}
                        >
                            {formatTimeAgo(result.lastUpdated)}
                        </Text>
                    </Group>
                    <SearchResultText
                        query={query}
                        text={result.matchingMessage.content}
                    />
                </Stack>
            </Group>
        </Spotlight.Action>
    ));

    useCSSHighlights(query, "[data-search-results]", [
        debouncedQuery,
        fulfilledTimeStamp,
    ]);

    return (
        <Spotlight.Root
            query={query}
            onQueryChange={setQuery}
            color={"gneis"}
            onSpotlightOpen={() => setIsOpen(true)}
            onSpotlightClose={() => {
                setIsOpen(false);
                setQuery("");
            }}
            overlayProps={{
                blur: 0,
            }}
            classNames={{
                inner: classes.spotlightInner,
            }}
        >
            <Spotlight.Search
                classNames={{
                    wrapper: classes.spotlightSearchWrapper,
                    input: classes.spotlightSearchInput,
                }}
                placeholder="Search..."
                rightSectionPointerEvents="visible"
                rightSection={
                    <ActionIcon
                        variant="transparent"
                        color="gneis.6"
                        size="sm"
                        onClick={() => spotlight.close()}
                    >
                        <IconX width={18} />
                    </ActionIcon>
                }
            />
            <Spotlight.ActionsList
                className={classes.spotlightActionsList}
                classNames={{
                    actionsListInner: classes.spotlightActionsListInner,
                }}
                mih={450}
                h={450}
            >
                {query.length > 0 &&
                    !isSearchFetching &&
                    searchResults?.items.length === 0 && (
                        <Text p="xs">{t("common.noResults")}</Text>
                    )}
                {query.length === 0 && defaultList}
                <Box
                    h="100%"
                    className={scrollClasses.scrollboxLightThick}
                    onScroll={handleScroll}
                >
                    <Stack gap={0}>
                        {query.length > 0 &&
                            !isSearchFetching &&
                            searchResultList}
                        {renderedPagingLoading}
                    </Stack>
                </Box>
                {renderedSkeletons}
            </Spotlight.ActionsList>
        </Spotlight.Root>
    );
}

const SearchResultText = ({query, text}: {query: string; text: string}) => {
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();
    const index = textLower.indexOf(queryLower);

    const lowestIndex = index ? Math.max(0, index - 8) : 0;

    return (
        <Text data-search-results className={classes.searchResultText}>
            {lowestIndex > 0 && <Text span>...</Text>}
            <ParagraphWithReferences
                data-search-results
                span
                className={classes.searchResultText}
            >
                {text.slice(lowestIndex, text.length)}
            </ParagraphWithReferences>
        </Text>
    );
};
